import { supabase } from '../lib/supabase';

// ─── Parse notes to extract pre-filled items ─────────────────────────────────
// Matches patterns like: "1x Carton petit 60×40×40 cm — 90 €" or "2x Barrique noire 270 L — 140 €"
const ADT_CATALOG_PRICES = {
  'Barrique noire 270 L transport seul': 140,
  'Barrique noire 270 L promo transport + barrique': 160,
  'Barrique bleue 220 L transport': 140,
  'Carton 60×50×40': 70,
  'Carton petit 60×40×40': 90,
  'Carton 80×50×50': 100,
  'Carton 100×50×50': 110,
  'Carton grand 100×80×80': 180,
  'Valise 23 kg': 70,
  'Sac cabas moyen': 90,
  'Sac cabas grand': 130,
  'TV 55 pouces': 300,
  'TV 82 pouces': 350,
  'TV 127 pouces': 450,
  'Réfrigérateur petit': 100,
  'Réfrigérateur moyen': 150,
  'Réfrigérateur grand': 180,
  'Lave-linge': 120,
  'Micro-ondes': 60,
  'Cuisinière': 130,
};

/**
 * Parse notes text to extract structured items
 * Handles patterns like:
 *   - "1x Carton petit 60×40×40 cm — 90 €" *   -"2x Carton grand 100×80×80 — 180 €" *   -"1 Carton petit 60×40×40 — 90€"
 */
function parseNotesToItems(notes) {
  if (!notes) return [];
  const items = [];
  const lines = notes?.split('\n');

  for (const line of lines) {
    const trimmed = line?.trim();
    if (!trimmed) continue;

    // Pattern: "Nx Label — price €" or "N x Label — price €"
    const match = trimmed?.match(/^(\d+)\s*[xX×]\s*(.+?)\s*[—\-–]\s*(\d+(?:[.,]\d+)?)\s*€?/);
    if (match) {
      const quantity = parseInt(match?.[1]) || 1;
      const rawLabel = match?.[2]?.trim();
      const unitPrice = parseFloat(match?.[3]?.replace(',', '.')) || 0;

      // Try to match against catalog
      let label = rawLabel;
      let category = 'divers';
      let dimensions = '';

      for (const [catalogLabel] of Object.entries(ADT_CATALOG_PRICES)) {
        if (rawLabel?.toLowerCase()?.includes(catalogLabel?.toLowerCase()) ||
            catalogLabel?.toLowerCase()?.includes(rawLabel?.toLowerCase())) {
          label = catalogLabel;
          break;
        }
      }

      // Detect category
      if (label?.toLowerCase()?.includes('barrique')) category = 'barrique';
      else if (label?.toLowerCase()?.includes('carton')) category = 'carton';
      else if (label?.toLowerCase()?.includes('valise') || label?.toLowerCase()?.includes('sac')) category = 'bagage';
      else if (label?.toLowerCase()?.includes('tv') || label?.toLowerCase()?.includes('réfrigérateur') ||
               label?.toLowerCase()?.includes('lave') || label?.toLowerCase()?.includes('micro') ||
               label?.toLowerCase()?.includes('cuisinière')) category = 'electromenager';

      // Extract dimensions from label
      const dimMatch = label?.match(/(\d+[×x]\d+[×x]\d+|\d+\s*kg|\d+\s*L|\d+\s*pouces)/i);
      if (dimMatch) dimensions = dimMatch?.[1];

      items?.push({
        id: Date.now() + Math.random(),
        label,
        quantity,
        unit_price: unitPrice,
        total: quantity * unitPrice,
        dimensions,
        category,
      });
    }
  }

  return items;
}

/**
 * Generate a tracking reference for a new shipment
 */
function generateTrackingReference() {
  const year = new Date()?.getFullYear()?.toString()?.slice(-2);
  const month = String(new Date()?.getMonth() + 1)?.padStart(2, '0');
  const random = Math.floor(Math.random() * 90000) + 10000;
  return `ADT${year}${month}${random}`;
}

export const driverPickupService = {
  /**
   * Get pickup tasks for a specific driver and date
   */
  async getPickupsForDriver(driverId, date) {
    const { data, error } = await supabase
      ?.from('pickup_tasks')
      ?.select(`
        *,
        shipment:shipments!pickup_tasks_shipment_id_fkey(reference)
      `)
      ?.eq('driver_id', driverId)
      ?.eq('date', date)
      ?.order('time_window', { ascending: true });

    if (error) throw error;

    return data?.map(pickup => ({
      id: pickup?.id,
      date: pickup?.date,
      timeWindow: pickup?.time_window,
      clientName: pickup?.client_name,
      clientPhone: pickup?.client_phone,
      clientAddress: pickup?.client_address,
      clientCity: pickup?.client_city,
      clientZone: pickup?.client_zone,
      status: pickup?.status,
      notes: pickup?.notes,
      shipmentId: pickup?.shipment_id,
      shipmentReference: pickup?.shipment?.reference,
      clientSignatureUrl: pickup?.client_signature_url,
      createdAt: pickup?.created_at,
      updatedAt: pickup?.updated_at
    })) || [];
  },

  /**
   * Get single pickup task details
   */
  async getPickupById(pickupId) {
    const { data, error } = await supabase
      ?.from('pickup_tasks')
      ?.select(`
        *,
        shipment:shipments!pickup_tasks_shipment_id_fkey(reference, description, current_status, photos_urls)
      `)
      ?.eq('id', pickupId)
      ?.single();

    if (error) throw error;

    // Parse collected_items from DB or fall back to parsing notes
    let collectedItems = data?.collected_items || [];
    if ((!collectedItems || collectedItems?.length === 0) && data?.notes) {
      collectedItems = parseNotesToItems(data?.notes);
    }

    return {
      id: data?.id,
      date: data?.date,
      timeWindow: data?.time_window,
      clientName: data?.client_name,
      clientPhone: data?.client_phone,
      clientAddress: data?.client_address,
      clientCity: data?.client_city,
      clientZone: data?.client_zone,
      clientEmail: data?.client_email || null,
      clientId: data?.client_id || null,
      status: data?.status,
      notes: data?.notes,
      shipmentId: data?.shipment_id,
      shipmentReference: data?.shipment?.reference,
      shipmentDescription: data?.shipment?.description,
      shipmentStatus: data?.shipment?.current_status,
      shipmentPhotosUrls: data?.shipment?.photos_urls || [],
      pickupPhotoUrls: data?.pickup_photo_urls || [],
      clientSignatureUrl: data?.client_signature_url,
      pickupSignatureUrl: data?.pickup_signature_url || null,
      collectedItems,
      paymentAmount: data?.payment_amount || null,
      paymentMethod: data?.payment_method || null,
      paymentNotes: data?.payment_notes || null,
      amountCollected: data?.amount_collected || null,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Update pickup task status
   */
  async updatePickupStatus(pickupId, status) {
    const { data, error } = await supabase
      ?.from('pickup_tasks')
      ?.update({ status })
      ?.eq('id', pickupId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Upload pickup photo
   */
  async uploadPickupPhoto(pickupId, photoBlob) {
    try {
      const { data: currentPickup, error: fetchError } = await supabase
        ?.from('pickup_tasks')
        ?.select('pickup_photo_urls, shipment_id')
        ?.eq('id', pickupId)
        ?.single();

      if (fetchError) throw new Error('Impossible de charger les informations de collecte');

      const currentPhotos = currentPickup?.pickup_photo_urls || [];
      if (currentPhotos?.length >= 5) throw new Error('Maximum 5 photos autorisées par collecte');

      const fileName = `${pickupId}-photo-${Date.now()}.jpg`;
      const filePath = `pickup-photos/${fileName}`;

      const { error: uploadError } = await supabase
        ?.storage
        ?.from('shipment-photos')
        ?.upload(filePath, photoBlob, { contentType: 'image/jpeg', upsert: false });

      if (uploadError) throw new Error('Échec du téléchargement de la photo');

      const updatedPhotos = [...currentPhotos, filePath];
      const { error: updateError } = await supabase
        ?.from('pickup_tasks')
        ?.update({ pickup_photo_urls: updatedPhotos })
        ?.eq('id', pickupId);

      if (updateError) throw new Error('Impossible de sauvegarder la photo dans la collecte');

      if (currentPickup?.shipment_id) {
        try {
          const { data: shipmentData } = await supabase
            ?.from('shipments')
            ?.select('pickup_photo_urls')
            ?.eq('id', currentPickup?.shipment_id)
            ?.single();

          if (shipmentData) {
            const currentShipmentPhotos = shipmentData?.pickup_photo_urls || [];
            await supabase
              ?.from('shipments')
              ?.update({ pickup_photo_urls: [...currentShipmentPhotos, filePath] })
              ?.eq('id', currentPickup?.shipment_id);
          }

          await supabase?.from('shipment_files')?.insert({
            shipment_id: currentPickup?.shipment_id,
            file_type: 'photo',
            file_url: filePath,
            file_name: fileName,
            bucket_name: 'shipment-photos'
          });
        } catch (shipmentError) {
          console.warn('⚠️ Could not update shipment photos (non-critical):', shipmentError);
        }
      }

      return filePath;
    } catch (error) {
      console.error('❌ Error uploading pickup photo:', error);
      throw error;
    }
  },

  /**
   * Delete a specific photo from pickup
   */
  async deletePickupPhoto(pickupId, filePath) {
    try {
      const { data: currentPickup, error: fetchError } = await supabase
        ?.from('pickup_tasks')
        ?.select('pickup_photo_urls, shipment_id')
        ?.eq('id', pickupId)
        ?.single();

      if (fetchError) throw new Error('Impossible de charger les informations de collecte');

      const currentPhotos = currentPickup?.pickup_photo_urls || [];
      const updatedPhotos = currentPhotos?.filter(photo => photo !== filePath);

      const { error: updateError } = await supabase
        ?.from('pickup_tasks')
        ?.update({ pickup_photo_urls: updatedPhotos })
        ?.eq('id', pickupId);

      if (updateError) throw new Error('Impossible de supprimer la photo de la collecte');

      try {
        await supabase?.storage?.from('shipment-photos')?.remove([filePath]);
      } catch (storageError) {
        console.warn('⚠️ Could not delete photo from storage (non-critical):', storageError);
      }

      if (currentPickup?.shipment_id) {
        try {
          const { data: shipmentData } = await supabase
            ?.from('shipments')
            ?.select('pickup_photo_urls')
            ?.eq('id', currentPickup?.shipment_id)
            ?.single();

          if (shipmentData) {
            const updatedShipmentPhotos = (shipmentData?.pickup_photo_urls || [])?.filter(p => p !== filePath);
            await supabase
              ?.from('shipments')
              ?.update({ pickup_photo_urls: updatedShipmentPhotos })
              ?.eq('id', currentPickup?.shipment_id);
          }
        } catch (shipmentError) {
          console.warn('⚠️ Could not update shipment photos (non-critical):', shipmentError);
        }
      }
    } catch (error) {
      console.error('❌ Error deleting pickup photo:', error);
      throw error;
    }
  },

  /**
   * Upload signature and mark pickup as completed
   */
  async uploadSignatureAndComplete(pickupId, signatureBlob) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const fileName = `${pickupId}-${Date.now()}.png`;
    const filePath = `signatures/${fileName}`;

    const { error: uploadError } = await supabase
      ?.storage
      ?.from('signatures')
      ?.upload(filePath, signatureBlob, { contentType: 'image/png', upsert: false });

    if (uploadError) throw uploadError;

    const { data: urlData, error: urlError } = await supabase
      ?.storage
      ?.from('signatures')
      ?.createSignedUrl(filePath, 31536000);

    if (urlError) throw urlError;

    const signatureUrl = urlData?.signedUrl;

    const { data: pickupData, error: updateError } = await supabase
      ?.from('pickup_tasks')
      ?.update({
        client_signature_url: signatureUrl,
        pickup_signature_url: signatureUrl,
        status: 'termine'
      })
      ?.eq('id', pickupId)
      ?.select('shipment_id, pickup_photo_urls, collected_items, payment_amount, payment_method')
      ?.single();

    if (updateError) throw updateError;

    if (pickupData?.shipment_id) {
      try {
        const updatePayload = { pickup_signature_url: signatureUrl };
        if (pickupData?.pickup_photo_urls?.length > 0) updatePayload.pickup_photo_urls = pickupData?.pickup_photo_urls;
        if (pickupData?.collected_items?.length > 0) updatePayload.collected_items = pickupData?.collected_items;
        await supabase?.from('shipments')?.update(updatePayload)?.eq('id', pickupData?.shipment_id);
      } catch (shipmentErr) {
        console.warn('⚠️ Could not copy data to shipments (non-critical):', shipmentErr);
      }
    }

    return signatureUrl;
  },

  /**
   * Find or create a client by phone (or email as fallback)
   * Returns the client_id
   */
  async findOrCreateClient({ name, phone, email, address, city }) {
    try {
      // 1. Try to find by phone first
      if (phone) {
        const { data: existingByPhone } = await supabase
          ?.from('clients')
          ?.select('id')
          ?.eq('phone', phone)
          ?.maybeSingle();

        if (existingByPhone?.id) {
          console.log('✅ Client found by phone:', existingByPhone?.id);
          return existingByPhone?.id;
        }
      }

      // 2. Try to find by email
      if (email) {
        const { data: existingByEmail } = await supabase
          ?.from('clients')
          ?.select('id')
          ?.eq('email', email)
          ?.maybeSingle();

        if (existingByEmail?.id) {
          console.log('✅ Client found by email:', existingByEmail?.id);
          return existingByEmail?.id;
        }
      }

      // 3. Create new client
      const newClientData = {
        name: name || 'Client inconnu',
        phone: phone || null,
        email: email || null,
        address: address || null,
        city: city || null,
      };

      const { data: newClient, error: createError } = await supabase
        ?.from('clients')
        ?.insert(newClientData)
        ?.select('id')
        ?.single();

      if (createError) {
        console.warn('⚠️ Could not create client:', createError);
        return null;
      }

      console.log('✅ New client created:', newClient?.id);
      return newClient?.id;
    } catch (err) {
      console.warn('⚠️ findOrCreateClient error (non-critical):', err);
      return null;
    }
  },

  /**
   * Create or update shipment from completed pickup
   * This is the core business logic: pickup → shipment
   */
  async createOrUpdateShipmentFromPickup({
    pickupId,
    pickupData,
    collectedItems,
    signatureUrl,
    photoUrls,
    payment,
    discount,
    driverId,
    clientId,
  }) {
    try {
      const totalAmount = collectedItems?.reduce((sum, item) => sum + (item?.total || 0), 0) || 0;
      const discountAmount = parseFloat(discount) || 0;
      const amountPaid = parseFloat(payment?.amountCollected) || 0;
      const totalAfterDiscount = totalAmount - discountAmount;
      const remainingAmount = Math.max(0, totalAfterDiscount - amountPaid);

      // Determine payment status
      let paymentStatus = 'non_paye';
      if (amountPaid >= totalAfterDiscount && totalAfterDiscount > 0) paymentStatus = 'paye';
      else if (amountPaid > 0) paymentStatus = 'partiel';

      // Clean items for storage (remove local id)
      const cleanItems = collectedItems?.map(({ id, ...rest }) => rest) || [];

      // If pickup already has a shipment_id, update it
      if (pickupData?.shipmentId) {
        const { error: updateError } = await supabase
          ?.from('shipments')
          ?.update({
            client_id: clientId || null,
            pickup_task_id: pickupId,
            driver_id: driverId || null,
            current_status: 'depose',
            pickup_signature_url: signatureUrl,
            pickup_photo_urls: photoUrls || [],
            collected_items: cleanItems,
            total_amount: totalAmount,
            discount_amount: discountAmount,
            paid_amount: amountPaid,
            remaining_amount: remainingAmount,
            payment_method: payment?.method || null,
            payment_notes: payment?.notes || null,
            payment_status: paymentStatus,
            pickup_completed_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString(),
          })
          ?.eq('id', pickupData?.shipmentId);

        if (updateError) throw updateError;
        console.log('✅ Existing shipment updated from pickup');
        return pickupData?.shipmentId;
      }

      // Create a new shipment
      const trackingRef = generateTrackingReference();

      const { data: newShipment, error: createError } = await supabase
        ?.from('shipments')
        ?.insert({
          reference: trackingRef,
          client_id: clientId || null,
          pickup_task_id: pickupId,
          sender_name: pickupData?.clientName || 'Client',
          sender_phone: pickupData?.clientPhone || '',
          sender_address: pickupData?.clientAddress || '',
          sender_city: pickupData?.clientCity || '',
          sender_country: 'France',
          receiver_name: pickupData?.clientName || 'Client',
          receiver_phone: pickupData?.clientPhone || '',
          receiver_address: '',
          receiver_city: '',
          receiver_country: 'Côte d\'Ivoire',
          current_status: 'depose',
          pickup_signature_url: signatureUrl,
          pickup_photo_urls: photoUrls || [],
          collected_items: cleanItems,
          total_amount: totalAmount,
          discount_amount: discountAmount,
          paid_amount: amountPaid,
          remaining_amount: remainingAmount,
          payment_method: payment?.method || null,
          payment_notes: payment?.notes || null,
          payment_status: paymentStatus,
          pickup_completed_at: new Date()?.toISOString(),
          description: pickupData?.notes || null,
          shipment_type: 'colis',
        })
        ?.select('id')
        ?.single();

      if (createError) throw createError;

      // Link the new shipment back to pickup_tasks
      await supabase
        ?.from('pickup_tasks')
        ?.update({ shipment_id: newShipment?.id, client_id: clientId || null })
        ?.eq('id', pickupId);

      console.log('✅ New shipment created from pickup:', newShipment?.id, 'ref:', trackingRef);
      return newShipment?.id;
    } catch (err) {
      console.error('❌ createOrUpdateShipmentFromPickup error:', err);
      throw err;
    }
  },
};

export default driverPickupService;